'use client';

import { ChevronDownIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';
import { AutocompleteOptionList } from './autocomplete-option-list';
import { Input } from './input';
import { Label } from './label';
import type { AutocompleteViewProps } from './use-autocomplete';

/**
 * Desktop rendering for `Autocomplete`: the (single or chip-based multi-select)
 * combobox input, trailing icons, and the absolutely-positioned dropdown.
 * Extracted from `autocomplete.tsx` (conformance, code-size guard).
 */
export function AutocompleteDesktop({ props, state }: AutocompleteViewProps) {
  const {
    options,
    className,
    label,
    required,
    multiple,
    inputClassName,
    disabled,
    error,
    placeholder,
    autoFocus,
    trailingAction,
    loading,
    hint,
    loadingMessage,
    header,
    renderSelected,
    maxSelections,
  } = props;
  const ariaLabel = props['aria-label'];
  const ariaLabelledBy = props['aria-labelledby'];
  // With an external id (a <label htmlFor> or FormControl), that label names
  // the input; a generic aria-label would override it. The listbox has no
  // such label, so it keeps `label || ariaLabel`.
  const inputAriaLabel =
    !label && !props.id && !ariaLabelledBy ? ariaLabel : undefined;
  // Merge a form wrapper's description and invalid state with our own error.
  const describedBy =
    [props['aria-describedby'], error ? `${state.baseId}-error` : null]
      .filter(Boolean)
      .join(' ') || undefined;
  // Also drives the destructive border, so a `FormControl` error (whose text
  // `FormMessage` renders) marks the field without a second message.
  const invalid =
    !!error ||
    props['aria-invalid'] === true ||
    props['aria-invalid'] === 'true';
  const {
    containerRef,
    labelId,
    inputId,
    inputRef,
    selectedValues,
    atLimit,
    handleSelect,
    isOpen,
    listboxId,
    highlightedIndex,
    baseId,
    inputValue,
    handleInputChange,
    handleKeyDown,
    handleInputFocus,
    openAndFocus,
    displayValue,
    handleClear,
    canCreate,
    filteredOptions,
    listRef,
    statusId,
  } = state;

  const showDropdown =
    isOpen && (filteredOptions.length > 0 || loading || canCreate);

  // Single-select rich summary: when the consumer supplies `renderSelected`,
  // a value is chosen, and the field is collapsed, show the summary in place of
  // the plain input (click to reopen). Opt-in — no consumer regresses.
  const selectedOption =
    !multiple && selectedValues[0]
      ? (options.find((o) => o.value === selectedValues[0]) ?? null)
      : null;
  const showSummary = !!renderSelected && !!selectedOption && !isOpen;

  return (
    <div ref={containerRef} className={cn('relative space-y-2', className)}>
      {label && (
        <Label id={labelId} htmlFor={inputId}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}

      {header && <div className="text-muted-foreground text-sm">{header}</div>}

      <div className="relative">
        {multiple ? (
          // Multi-select: selected values render as removable chips INSIDE the
          // field (wrapping), with the type-to-filter input flowing after them.
          // Consumers must NOT render a separate badge strip below.
          <div
            onClick={() => inputRef.current?.focus()}
            className={cn(
              'border-input bg-background flex min-h-11 w-full flex-wrap items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm',
              'focus-within:border-ring focus-within:ring-ring/40 transition-[color,box-shadow] focus-within:ring-[3px] focus-within:outline-none',
              trailingAction ? 'pr-16' : 'pr-10',
              disabled && 'cursor-not-allowed opacity-50',
              invalid && 'border-destructive',
              inputClassName,
            )}
          >
            {selectedValues.map((v) => {
              const opt = options.find((o) => o.value === v);
              const chipLabel = opt?.label ?? v;
              return (
                <span
                  key={v}
                  className="bg-muted text-foreground inline-flex max-w-full items-center gap-1 rounded-sm py-0.5 pr-1 pl-1.5"
                >
                  {opt?.icon && (
                    <span className="flex-shrink-0">{opt.icon}</span>
                  )}
                  <span className="truncate">{chipLabel}</span>
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(v);
                    }}
                    aria-label={`Remove ${chipLabel}`}
                    className="hover:bg-foreground/10 text-muted-foreground hover:text-foreground rounded-sm p-0.5 transition-colors"
                  >
                    <Cross2Icon className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
            {/* eslint-disable-next-line single-select/no-input-trigger -- the multi-select filter field: chips show the value, this input only filters */}
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                highlightedIndex >= 0
                  ? `${baseId}-option-${highlightedIndex}`
                  : undefined
              }
              aria-labelledby={labelId || ariaLabelledBy}
              aria-label={inputAriaLabel}
              aria-invalid={invalid}
              aria-describedby={describedBy}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              placeholder={selectedValues.length === 0 ? placeholder : ''}
              disabled={disabled}
              autoFocus={autoFocus}
              autoComplete="off"
              className="placeholder:text-muted-foreground h-7 min-w-[6rem] flex-1 bg-transparent outline-none disabled:cursor-not-allowed"
            />
          </div>
        ) : showSummary && selectedOption ? (
          <button
            type="button"
            onClick={openAndFocus}
            // The collapsed summary stands in for the input while a value is
            // held, so the form wrapper's id and description belong here too.
            id={inputId}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-label={inputAriaLabel}
            className={cn(
              'border-input bg-background flex min-h-11 w-full items-center rounded-md border px-3 py-1.5 text-left text-sm',
              'focus-visible:border-ring focus-visible:ring-ring/40 transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-none',
              trailingAction ? 'pr-16' : 'pr-10',
              disabled && 'cursor-not-allowed opacity-50',
              invalid && 'border-destructive',
              inputClassName,
            )}
          >
            {renderSelected(selectedOption)}
          </button>
        ) : (
          // eslint-disable-next-line single-select/no-input-trigger -- Autocomplete is the sanctioned free-text combobox; single-select option pickers are flagged at their <Autocomplete> call site (BAL-1132)
          <Input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${baseId}-option-${highlightedIndex}`
                : undefined
            }
            aria-labelledby={labelId || ariaLabelledBy}
            aria-label={inputAriaLabel}
            aria-invalid={invalid}
            aria-describedby={describedBy}
            value={displayValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            autoComplete="off"
            className={cn(
              'h-11',
              trailingAction ? 'pr-16' : 'pr-10',
              invalid && 'border-destructive',
              inputClassName,
            )}
          />
        )}

        {/* Right side icons */}
        <div
          className={cn(
            'absolute right-3 flex items-center gap-1',
            multiple ? 'top-3' : 'top-1/2 -translate-y-1/2',
          )}
        >
          {trailingAction}
          {loading ? (
            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
          ) : (
            <>
              {selectedValues.length > 0 && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="hover:bg-muted rounded-sm p-0.5"
                  aria-label="Clear selection"
                  tabIndex={-1}
                >
                  <Cross2Icon className="h-3.5 w-3.5" />
                </button>
              )}
              <ChevronDownIcon
                className={cn(
                  'text-muted-foreground h-4 w-4 transition-transform',
                  isOpen && 'rotate-180',
                )}
              />
            </>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={label || ariaLabel}
          className={cn(
            'bg-popover text-popover-foreground absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
          )}
        >
          <AutocompleteOptionList props={props} state={state} />
        </div>
      )}

      {/* Selection cap counter (multiple + maxSelections) */}
      {multiple && maxSelections ? (
        <p
          className={cn(
            'text-xs',
            atLimit ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {selectedValues.length} / {maxSelections}
        </p>
      ) : null}

      {/* Error/Hint */}
      {hint && !error && (
        <p className="text-muted-foreground text-sm">{hint}</p>
      )}
      {error && (
        <p id={`${baseId}-error`} className="text-destructive text-sm">
          {error}
        </p>
      )}

      {/* Screen reader announcements */}
      <div
        id={statusId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {loading
          ? loadingMessage
          : isOpen
            ? `${filteredOptions.length} results available${
                highlightedIndex >= 0
                  ? `. Item ${highlightedIndex + 1} of ${filteredOptions.length}`
                  : ''
              }`
            : ''}
      </div>
    </div>
  );
}
